import React, { useState, useEffect, useCallback } from 'react';
import { Table, Form, Button, Spinner, Row, Col } from 'react-bootstrap';
import api from '../../config/roundrobinApi';

export default function LogsTab() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [loading, setLoading] = useState(false);

  const [agentFilter, setAgentFilter] = useState('');
  const [queueFilter, setQueueFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [queues, setQueues] = useState([]);

  useEffect(() => {
    api.get('/queues').then(res => setQueues(res.data)).catch(() => {});
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (agentFilter) params.agent_zendesk_id = agentFilter;
      if (queueFilter) params.queue_id = queueFilter;
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo) params.date_to = new Date(dateTo).toISOString();
      const res = await api.get('/assignment-logs', { params });
      setLogs(res.data.items);
      setTotal(res.data.total);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [page, pageSize, agentFilter, queueFilter, dateFrom, dateTo]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const clearFilters = () => {
    setAgentFilter('');
    setQueueFilter('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  return (
    <>
      <Row className="mb-3 g-2 align-items-end">
        <Col md={2}>
          <Form.Label className="small mb-1">Agent ID</Form.Label>
          <Form.Control size="sm" placeholder="Agent Zendesk ID" value={agentFilter} onChange={e => { setAgentFilter(e.target.value); setPage(1); }} />
        </Col>
        <Col md={2}>
          <Form.Label className="small mb-1">Queue</Form.Label>
          <Form.Select size="sm" value={queueFilter} onChange={e => { setQueueFilter(e.target.value); setPage(1); }}>
            <option value="">All Queues</option>
            {queues.map(q => <option key={q.id} value={q.id}>{q.name}</option>)}
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Label className="small mb-1">From</Form.Label>
          <Form.Control size="sm" type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }} />
        </Col>
        <Col md={2}>
          <Form.Label className="small mb-1">To</Form.Label>
          <Form.Control size="sm" type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }} />
        </Col>
        <Col md={2}>
          <Button variant="outline-secondary" size="sm" onClick={clearFilters}>Clear Filters</Button>
        </Col>
        <Col md={2} className="text-end">
          <small className="text-muted">{total} total records</small>
        </Col>
      </Row>

      {loading ? <div className="text-center py-5"><Spinner /></div> : (
        <Table hover responsive size="sm">
          <thead className="table-light">
            <tr>
              <th>Assigned At</th>
              <th>Ticket ID</th>
              <th>Requester ID</th>
              <th>Agent</th>
              <th>Queue</th>
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 ? (
              <tr><td colSpan={5} className="text-center text-muted py-4">No assignment logs found</td></tr>
            ) : logs.map(log => (
              <tr key={log.id}>
                <td><small>{log.assigned_at ? new Date(log.assigned_at).toLocaleString() : '-'}</small></td>
                <td>{log.ticket_id || '-'}</td>
                <td>{log.ticket_requester_id || '-'}</td>
                <td>{log.agent_name || log.agent_zendesk_id || '-'}</td>
                <td>{log.queue_name || '-'}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-2">
          <Button variant="outline-primary" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
            &laquo; Previous
          </Button>
          <small className="text-muted">Page {page} of {totalPages}</small>
          <Button variant="outline-primary" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
            Next &raquo;
          </Button>
        </div>
      )}
    </>
  );
}
